import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/ui/PageHeader';
import { StatusBadge } from '@/ui/StatusBadge';
import { Button } from '@/ui/base/button';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { usePortalPaginas, type PortalPagina } from '@/lib/usePortalContent';
import { avisarAcaoIndisponivel } from '@/modulos/admin/portal-publico/avisoIndisponivel';
import { Globe, Pencil, Eye, EyeOff } from 'lucide-react';

export default function PortalPaginasList() {
  const { data: paginas, isLoading } = usePortalPaginas();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<Record<string, string>>({ q: '', status: '' });

  const filtered = useMemo(() => {
    const list = paginas ?? [];
    const q = (filters.q ?? '').trim().toLowerCase();
    return list.filter((p: PortalPagina) => {
      if (q && !`${p.titulo} ${p.slug} ${p.meta_title ?? ''}`.toLowerCase().includes(q))
        return false;
      if (filters.status === 'ativo' && !p.ativo) return false;
      if (filters.status === 'inativo' && p.ativo) return false;
      return true;
    });
  }, [paginas, filters]);

  const filterFields: FilterFieldConfig[] = [
    { key: 'q', label: 'Buscar', type: 'search', placeholder: 'Título, slug ou SEO...' },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'ativo', label: 'Ativo' },
        { value: 'inativo', label: 'Inativo' },
      ],
    },
  ];

  const toggleAtivo = (e: React.MouseEvent, _id: string, _ativo: boolean) => {
    e.stopPropagation();
    avisarAcaoIndisponivel();
  };

  const columns: DataTableColumn<PortalPagina>[] = [
    { header: 'Ordem', cell: (p) => p.ordem, sortKey: 'ordem' },
    {
      header: 'Slug',
      cell: (p) => <span className="font-mono text-xs">{p.slug}</span>,
      sortKey: 'slug',
    },
    {
      header: 'Título',
      cell: (p) => <span className="font-medium">{p.titulo}</span>,
      sortKey: 'titulo',
    },
    {
      header: 'SEO Title',
      cell: (p) => (
        <span className="text-xs text-muted-foreground truncate max-w-[200px] block">
          {p.meta_title || <span className="italic opacity-50">—</span>}
        </span>
      ),
    },
    {
      header: 'Status',
      cell: (p) => <StatusBadge status={p.ativo ? 'ativo' : 'inativo'} />,
      sortKey: (p: PortalPagina) => (p.ativo ? 1 : 0),
    },
    {
      header: '',
      headerClassName: 'w-20',
      cell: (p) => (
        <div className="flex gap-1" data-row-stop>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate(`/admin/portal/paginas/${p.slug}`)}
            aria-label="Editar"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => toggleAtivo(e, p.id, p.ativo)}
            aria-label="Ocultar"
          >
            {p.ativo ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Páginas do Portal"
        description="Gerencie o conteúdo e SEO das páginas do portal público TEG."
      />

      <FilterBar
        fields={filterFields}
        values={filters}
        onChange={(k, v) => setFilters((p) => ({ ...p, [k]: v }))}
        onClear={() => setFilters({ q: '', status: '' })}
      />

      <DataTable
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        onRowClick={(p) => navigate(`/admin/portal/paginas/${p.slug}`)}
        emptyIcon={Globe}
        emptyTitle="Nenhuma página cadastrada"
        emptyDescription="As páginas do portal serão listadas aqui."
        showPagination={false}
        ariaLabel="Lista de páginas do portal"
      />
    </div>
  );
}
